/* eslint-disable camelcase */
import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import moment from "moment";
import { Tag } from "antd";
import { Button } from "@/components/ui/button";
import { tableBody_2, tableHeader2 } from "@/styles/TableStyles";
import { useGetRespondersByCompanyId } from "@/hooks/useResponder";
import Spin from "../widgets/Spinner/Spin";
import EditResponderDrawer from "../Drawers/EditResponderDrawer";
import { data } from "../Charts/EmergencyOperators/EmergencyOperatorsLine";

type ResponderTableProps = {
  companyId: number;
};

type ResponderStatusColor = {
  bgColor: string;
  textColor: string;
};

const ResponderTable: React.FC<ResponderTableProps> = ({ companyId }) => {
  const [first, setFirst] = useState(0);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedResponder, setSelectedResponder] =
    useState<IResponderDetails | null>(null);

  const { data: responseData, isLoading } = useGetRespondersByCompanyId(
    page,
    rows,
    companyId,
  );

  useEffect(() => {
    // reset page and row
    setFirst(0);
    setRows(10);
    setPage(1);
  }, [companyId]);

  const onPage = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
    setPage(event.page + 1);
  };

  const handleOpenDrawer = (responder: IResponderDetails) => {
    setSelectedResponder(responder);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedResponder(null);
  };

  const getSeverity = (
    ApplicationStatus: ResponderStatus,
  ): ResponderStatusColor => {
    switch (ApplicationStatus) {
      case "Active":
        return { bgColor: "#D2F6C1", textColor: "#369A03" };
      case "Expired":
        return { bgColor: "bg-orange-400", textColor: "text-orange-900" };
      case "Suspended":
        return { bgColor: "#FDD6C7", textColor: "#CC3201" };
      case "Pending":
        return { bgColor: "#FEF3C7", textColor: "#D97706" };
      default:
        return { bgColor: "#D2F6C1", textColor: "#369A03" };
    }
  };

  const statusBodyTemplate = (rowData: IResponderDetails) => {
    // Defensive programming: ensure panicTypes is an array
    const panicTypes = Array.isArray(rowData.panicTypes)
      ? rowData.panicTypes
      : [];

    return (
      <div>
        {panicTypes.map((panicType, index) => {
          const color = getSeverity(panicType.status);
          return (
            <Tag
              key={index}
              color={color.bgColor}
              className={`${color.bgColor} px-2.5 py-0.5 rounded`}
            >
              <p
                className={`${color.textColor} flex justify-center items-center mx-0 my-0`}
                style={{ color: `${color.textColor}` }}
              >
                {panicType.name}-{panicType.status}
              </p>
            </Tag>
          );
        })}
      </div>
    );
  };

  const dateBodyTemplate = (rowData: IResponderDetails) => {
    return moment(rowData.createdAt).format("YYYY-MM-DD");
  };

  const actionBodyTemplate = (rowData: IResponderDetails) => {
    return (
      <Button
        variant="link"
        className="min-w-12 text-appMainColor"
        onClick={() => handleOpenDrawer(rowData)}
      >
        View More
      </Button>
    );
  };

  return (
    <>
      <Spin tip="Fetching responders" spinning={isLoading}>
        <DataTable
          value={
            Array.isArray(responseData?.data?.data)
              ? responseData.data.data
              : []
          }
          totalRecords={responseData?.data?.totalCount || 0}
          paginator
          first={first}
          rows={rows}
          lazy
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPage={onPage}
          dataKey="id"
          emptyMessage="No responders found."
          rowHover
          scrollable
          scrollHeight="45rem"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} responders"
          stateStorage="session"
          stateKey="dt-state-responders-table"
          className="mx-3"
        >
          <Column
            field="firstName"
            header="First name"
            headerStyle={tableHeader2}
            bodyStyle={tableBody_2}
            className="bg-transparent"
          />
          <Column
            field="lastName"
            header="Last name"
            headerStyle={tableHeader2}
            bodyStyle={tableBody_2}
            className="bg-transparent"
          />
          <Column
            field="companyName"
            header="Responders"
            headerStyle={tableHeader2}
            bodyStyle={tableBody_2}
            className="bg-transparent"
          />
          <Column
            field="emailAddress"
            header="Email"
            headerStyle={tableHeader2}
            bodyStyle={tableBody_2}
            className="bg-transparent"
          />
          <Column
            field="status"
            header="Status"
            headerStyle={tableHeader2}
            bodyStyle={tableBody_2}
            className="bg-transparent"
            body={statusBodyTemplate}
          />
          <Column
            field="createdAt"
            header="Created At"
            filterField="date"
            dataType="date"
            headerStyle={tableHeader2}
            bodyStyle={tableBody_2}
            className="bg-transparent"
            body={dateBodyTemplate}
          />
          <Column
            exportable={false}
            body={actionBodyTemplate}
            headerStyle={tableHeader2}
            bodyStyle={tableBody_2}
            className="bg-transparent"
          />
        </DataTable>
      </Spin>

      <EditResponderDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        editDetails={selectedResponder}
      />
    </>
  );
};

export default ResponderTable;
